import { CaretRight } from "@phosphor-icons/react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useContext,
} from "react";

import Checkbox from "common/components/ui/Checkbox";

import {
  type DropdownMenuItemVariant,
  type DropdownMenuSize,
  dropdownMenuContentVariants,
  dropdownMenuItemVariants,
  dropdownMenuLabelClassname,
  dropdownMenuListVariants,
} from "./variants";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuSubTrigger = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger>
>(({ className, children, ...props }, ref) => {
  const size = useContext(DropdownMenuContentSizeContext);

  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={clsx(
        dropdownMenuItemVariants({ variant: "default", size }),
        "data-[state=open]:bg-black-100",
        className,
      )}
      {...props}
    >
      {children}
      <CaretRight size={16} className="ml-auto size-4" />
    </DropdownMenuPrimitive.SubTrigger>
  );
});
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

interface DropdownMenuSubContentProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> {
  size?: DropdownMenuSize;
}

const DropdownMenuSubContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>(({ className, size = "md", children, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={dropdownMenuContentVariants({ size, className })}
      {...props}
    >
      <DropdownMenuContentSizeContext.Provider value={size}>
        {children}
      </DropdownMenuContentSizeContext.Provider>
    </DropdownMenuPrimitive.SubContent>
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContentSizeContext = createContext<
  DropdownMenuSize | undefined
>(undefined);

interface DropdownMenuItemProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  variant?: DropdownMenuItemVariant;
  isSubItem?: boolean;
}

function DropdownMenuItemComponent(
  { className, variant, isSubItem = false, ...props }: DropdownMenuItemProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const size = useContext(DropdownMenuContentSizeContext);

  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={clsx(
        dropdownMenuItemVariants({ variant, size }),
        className,
        isSubItem && "pl-6",
      )}
      {...props}
    />
  );
}

DropdownMenuItemComponent.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuItem = forwardRef(DropdownMenuItemComponent);

const DropdownMenuLabel = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={clsx(dropdownMenuLabelClassname, className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={clsx("my-1 h-px bg-black-200", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

function DropdownMenuCheckbox({
  disabled = false,
  isChecked,
  onCheckedChange,
}: {
  disabled?: boolean;
  isChecked: boolean | "indeterminate";
  onCheckedChange: () => void;
}) {
  return (
    <Checkbox
      disabled={disabled}
      checked={isChecked}
      onClick={(e) => e.stopPropagation()}
      onCheckedChange={onCheckedChange}
      size="sm"
    />
  );
}

function DropdownMenuCount({ children }: { children: string | number }) {
  return <span className="ml-auto text-xs text-black-500">{children}</span>;
}

function DropdownMenuList({
  children,
  className,
  isScrollable = true,
}: {
  children: ReactNode;
  className?: string;
  isScrollable?: boolean;
}) {
  const size = useContext(DropdownMenuContentSizeContext);
  const isEmpty = Array.isArray(children) ? !children.length : !children;

  return (
    <div
      className={dropdownMenuListVariants({
        className,
        size,
        isHeightLimited: isScrollable,
      })}
    >
      {isEmpty ? (
        <p className="text-black-500 my-12 text-center">No results found</p>
      ) : (
        children
      )}
    </div>
  );
}
DropdownMenuList.displayName = "DropdownMenuList";

interface DropdownMenuContentProps
  extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {
  size?: DropdownMenuSize;
}

const DropdownMenuContent = forwardRef<
  ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 4, size, children, ...props }, ref) => {
  //children must be DropdownMenuList, MenuSearchInput, DropdownMenuSeparator, DropdownMenuHeader, DropdownMenuFooter.
  //TODO: add validation

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={dropdownMenuContentVariants({ size, className })}
        {...props}
      >
        <DropdownMenuContentSizeContext.Provider value={size}>
          {children}
        </DropdownMenuContentSizeContext.Provider>
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

function DropdownMenuFooter({ children }: { children: ReactNode }) {
  const size = useContext(DropdownMenuContentSizeContext);

  return (
    <div
      className={clsx(
        "border-t border-t-black-200",
        size === "sm" ? "px-1 pt-1" : "pt-1.5 px-1.5",
      )}
    >
      {children}
    </div>
  );
}
DropdownMenuFooter.displayName = "DropdownMenuFooter";

function DropdownMenuHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx("border-b border-b-black-200 py-2 px-3", className)}>
      {children}
    </div>
  );
}
DropdownMenuHeader.displayName = "DropdownMenuHeader";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuCount,
  DropdownMenuCheckbox,
  DropdownMenuList,
  DropdownMenuFooter,
  DropdownMenuHeader,
};
